<template>
  <div class="bundle-price-rule">
    <div class="rule-section">
      <p class="rule-section-title text-white">Bundle configuration</p>

      <div class="row">
        <div class="col-md-4">
          <base-input label="Minimum to qualify">
            <el-input-number
              v-model="localConfig.min_quantity"
              :min="1"
              :max="999"
              @change="onQuantityChange"
            ></el-input-number>
          </base-input>
        </div>
        <div class="col-md-4">
          <base-input label="How many get the price">
            <el-input-number
              v-model="localConfig.affected_quantity"
              :min="1"
              :max="999"
              @change="emitUpdate"
            ></el-input-number>
          </base-input>
        </div>
        <div class="col-md-4">
          <base-input label="Bundle price">
            <money-input
              :value="localConfig.bundle_price"
              :min="0"
              :precision="2"
              @input="onPriceInput"
            ></money-input>
          </base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <base-input label="Price applies to">
            <segmented-toggle
              variant="pills"
              size="sm"
              :options="modeOptions"
              :value="localConfig.price_mode"
              @input="onModeChange"
            ></segmented-toggle>
          </base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-12 d-flex align-items-center mb-3">
          <el-switch
            v-model="localConfig.allow_reapplication"
            active-color="#e14eca"
            @change="emitUpdate"
          ></el-switch>
          <span class="ml-2 text-white">Reapply for each full group</span>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <p class="small mb-0 helper-text text-white">
            <i class="tim-icons el-icon-info mr-1"></i>
            <span v-if="localConfig.price_mode === 'cart_total'">
              Each full group of {{ localConfig.affected_quantity || 1 }} qualifying items is priced at the bundle price together, and the <strong>highest priced</strong> items are bundled first. {{ reapplyHint }}
            </span>
            <span v-else-if="localConfig.price_mode === 'quantity'">
              Each affected item is priced at the bundle price per unit, and the <strong>highest priced</strong> items are affected first. {{ reapplyHint }}
            </span>
            <span v-else>
              Each affected cart line is priced at the bundle price, and the <strong>highest priced</strong> lines are affected first. {{ reapplyHint }}
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import MoneyInput from '@/components/Inputs/MoneyInput.vue'

interface BundlePriceConfig {
  min_quantity: number
  affected_quantity: number
  bundle_price: number | null
  price_mode: 'cart_total' | 'line_item' | 'quantity'
  allow_reapplication: boolean
  error_message: string
}

const props = defineProps({
  ruleConfig: { type: Object, required: true }
})

const emit = defineEmits<{
  (e: 'update', config: BundlePriceConfig): void
}>()

const modeOptions = [
  { value: 'cart_total', label: 'Group total' },
  { value: 'line_item', label: 'Per line' },
  { value: 'quantity', label: 'Per unit' }
]

const localConfig = ref<BundlePriceConfig>({
  min_quantity: 1,
  affected_quantity: 1,
  bundle_price: null,
  price_mode: 'cart_total',
  allow_reapplication: false,
  error_message: ''
})

const reapplyHint = computed(() =>
  localConfig.value.allow_reapplication
    ? 'The bundle repeats for every additional full group; leftover items stay at full price.'
    : 'The bundle applies once; remaining items stay at full price.'
)

const initialized = ref(false)
watch(
  () => props.ruleConfig,
  (newVal) => {
    if (newVal && !initialized.value) {
      localConfig.value = { ...localConfig.value, ...(newVal as Partial<BundlePriceConfig>) }
      initialized.value = true
    }
  },
  { immediate: true, deep: true }
)

function onQuantityChange(value: number, oldValue: number) {
  // The typical bundle is "buy N for $X" where N is both the trigger and the
  // priced group: track the qualifier in both directions while the two match,
  // and never let the priced count exceed the qualifier (the save guard
  // rejects that state, so do not create it here).
  if (localConfig.value.affected_quantity === oldValue) {
    localConfig.value.affected_quantity = localConfig.value.min_quantity
  } else if (localConfig.value.affected_quantity > localConfig.value.min_quantity) {
    localConfig.value.affected_quantity = localConfig.value.min_quantity
  }
  emitUpdate()
}

function onModeChange(mode: BundlePriceConfig['price_mode']) {
  localConfig.value.price_mode = mode
  emitUpdate()
}

function onPriceInput(value: number | null) {
  localConfig.value.bundle_price = value
  emitUpdate()
}

function emitUpdate() {
  emit('update', { ...localConfig.value })
}
</script>

<style scoped>
.rule-section {
  padding: 1rem 1.25rem;
  background: rgba(128, 128, 128, 0.08);
  border-radius: 0.375rem;
  border-left: 3px solid #e14eca;
  margin-bottom: 1rem;
}

.rule-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1rem 0;
  color: #fff;
}

.bundle-price-rule >>> label {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.bundle-price-rule >>> .form-group label {
  color: rgba(255, 255, 255, 0.8);
}

.helper-text {
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
}
</style>
